/** First-login onboarding wizard for self-serve learners.
 *
 * Five quick self-report questions (goal, exam board, current level, focus
 * level, pace). No selling here and no graded test — the answers drive a
 * recommendation the learner sees on the dashboard next, and can freely
 * change in the store. The current-level step offers an optional
 * plain-language "help me choose" so someone unsure of their CEFR letter can
 * still place themselves.
 *
 * On submit we POST the answers (which stamps the account onboarded) and send
 * the learner to their dashboard, where the recommendation card greets them.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../../api/http";
import { submitOnboarding } from "../../api/onboarding";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/ui";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";

interface Option {
  value: string;
  label: MessageKey;
}

const GOALS: Option[] = [
  { value: "exam", label: "onboarding.goal.exam" },
  { value: "work", label: "onboarding.goal.work" },
  { value: "study", label: "onboarding.goal.study" },
  { value: "immigration", label: "onboarding.goal.immigration" },
  { value: "travel", label: "onboarding.goal.travel" },
];

const BOARDS: Option[] = [
  { value: "telc", label: "onboarding.board.telc" },
  { value: "goethe", label: "onboarding.board.goethe" },
  { value: "either", label: "onboarding.board.either" },
];

const LEVELS: Option[] = [
  { value: "none", label: "onboarding.level.none" },
  { value: "A1", label: "onboarding.level.A1" },
  { value: "A2", label: "onboarding.level.A2" },
  { value: "B1", label: "onboarding.level.B1" },
  { value: "B2", label: "onboarding.level.B2" },
  { value: "C1", label: "onboarding.level.C1" },
];

// Plain-language can-do statements, one per current-level value — the
// optional "quick check". Picking one sets the current level.
const QUICK_CHECK: Option[] = [
  { value: "none", label: "onboarding.quick.none" },
  { value: "A1", label: "onboarding.quick.A1" },
  { value: "A2", label: "onboarding.quick.A2" },
  { value: "B1", label: "onboarding.quick.B1" },
  { value: "B2", label: "onboarding.quick.B2" },
  { value: "C1", label: "onboarding.quick.C1" },
];

const FOCUS_LEVELS = ["A1", "A2", "B1", "B2", "C1"];

const TIMELINES: Option[] = [
  { value: "relaxed", label: "onboarding.timeline.relaxed" },
  { value: "steady", label: "onboarding.timeline.steady" },
  { value: "intensive", label: "onboarding.timeline.intensive" },
];

// A complete beginner starts at A1; otherwise focus defaults to the current
// level (solidify it + sit its exam). Mirrors the backend engine.
const CURRENT_TO_FOCUS: Record<string, string> = {
  none: "A1", A1: "A1", A2: "A2", B1: "B1", B2: "B2", C1: "C1",
};

const TOTAL_STEPS = 5;

export function OnboardingPage() {
  const t = useT();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState("");
  const [board, setBoard] = useState("");
  const [level, setLevel] = useState("");
  const [focus, setFocus] = useState("");
  const [timeline, setTimeline] = useState("steady");
  const [quickOpen, setQuickOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Choosing a current level pre-selects the matching focus level so the
  // next step already has the sensible default highlighted.
  const chooseLevel = (value: string) => {
    setLevel(value);
    setFocus(CURRENT_TO_FOCUS[value] ?? "A1");
  };

  const stepReady = [
    goal !== "",
    board !== "",
    level !== "",
    focus !== "",
    timeline !== "",
  ][step];

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      await submitOnboarding({ goal, level, target_level: focus, board, timeline });
      // Re-hydrate the session so `onboarded` flips true and the gate lets
      // us through to the dashboard instead of bouncing back here.
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
      setBusy(false);
    }
  };

  const next = () => {
    if (step < TOTAL_STEPS - 1) setStep((s) => s + 1);
    else void submit();
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <p className="text-sm font-semibold uppercase tracking-widest text-bronze-dark">
          {t("app.name")}
        </p>
        <h1 className="mt-2 text-2xl font-extrabold text-balance sm:text-3xl">
          {t("onboarding.title")}
        </h1>
        <p className="mt-2 text-muted">{t("onboarding.subtitle")}</p>

        {/* progress */}
        <div className="mt-6 flex items-center gap-2" aria-hidden>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand" : "bg-line"}`}
            />
          ))}
        </div>
        <p className="mt-2 text-xs text-muted">
          {t("onboarding.stepOf", { n: step + 1, total: TOTAL_STEPS })}
        </p>

        <div className="mt-6">
          {step === 0 && (
            <Question title={t("onboarding.q.goal")}>
              <OptionGrid options={GOALS} selected={goal} onSelect={setGoal} t={t} />
            </Question>
          )}

          {step === 1 && (
            <Question title={t("onboarding.q.board")}>
              <OptionGrid options={BOARDS} selected={board} onSelect={setBoard} t={t} />
            </Question>
          )}

          {step === 2 && (
            <Question title={t("onboarding.q.level")}>
              <OptionGrid options={LEVELS} selected={level} onSelect={chooseLevel} t={t} />
              <button
                type="button"
                onClick={() => setQuickOpen((o) => !o)}
                className="mt-3 text-sm font-medium text-brand hover:underline"
                aria-expanded={quickOpen}
              >
                {t("onboarding.quickCheck")}
              </button>
              {quickOpen && (
                <div className="mt-3 rounded-card border border-line bg-surface p-4">
                  <p className="mb-2 text-sm font-semibold text-ink">
                    {t("onboarding.quickCheck.q")}
                  </p>
                  <OptionGrid options={QUICK_CHECK} selected={level} onSelect={chooseLevel} t={t} />
                </div>
              )}
            </Question>
          )}

          {step === 3 && (
            <Question title={t("onboarding.q.focus")}>
              <div className="flex flex-wrap gap-2">
                {FOCUS_LEVELS.map((lv) => (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => setFocus(lv)}
                    aria-pressed={focus === lv}
                    className={`rounded-control px-5 py-3 font-bold ${
                      focus === lv
                        ? "bg-brand text-white"
                        : "border border-line bg-surface text-ink hover:border-brand"
                    }`}
                  >
                    {lv}
                  </button>
                ))}
              </div>
            </Question>
          )}

          {step === 4 && (
            <Question title={t("onboarding.q.timeline")}>
              <OptionGrid options={TIMELINES} selected={timeline} onSelect={setTimeline} t={t} />
            </Question>
          )}
        </div>

        {error && <p role="alert" className="mt-4 text-sm text-danger">{error}</p>}

        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="text-sm font-semibold text-muted disabled:opacity-0 hover:text-ink"
          >
            ← {t("onboarding.back")}
          </button>
          <Button onClick={next} disabled={!stepReady} loading={busy}>
            {step === TOTAL_STEPS - 1 ? t("onboarding.finish") : t("onboarding.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Question({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-ink">{title}</h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function OptionGrid({
  options,
  selected,
  onSelect,
  t,
}: {
  options: Option[];
  selected: string;
  onSelect: (value: string) => void;
  t: (key: MessageKey) => string;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onSelect(o.value)}
          aria-pressed={selected === o.value}
          className={`rounded-card border p-3 text-left text-sm font-medium transition-colors ${
            selected === o.value
              ? "border-brand bg-brand-soft text-brand-dark"
              : "border-line bg-surface text-ink hover:border-brand"
          }`}
        >
          {t(o.label)}
        </button>
      ))}
    </div>
  );
}
